import { useLocation } from "@reach/router";
import { cn } from "@babybjorn/ui";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useCallback, useEffect, useState, type FC } from "react";
import Button from "../../components/general/Button";
import Divider from "../../components/general/Divider";
import { AppDispatchContext, useAppState } from "../../context/app/context";
import { useCartProducts } from "../../hooks/use-cart-products";
import { useDispatchContext } from "../../hooks/use-dispatch-context";
import { useTranslatedPaths } from "../../hooks/use-translated-paths";
import { formatVariantPrice } from "../../utils/cart/currency";
import {
	getVariantColorLabelFromFragment,
	getVariantFabricLabelFromFragment,
	getVariantProductImages,
} from "../../utils/storyblok/product-variant";
import ModalOverlay from "../general/Modal/components/ModalOverlay/ModalOverlay";
import * as s from "./AddToCartToaster.module.scss";
import ToasterItem from "./components/ToasterItem";
import CartIndicator from "./components/ToasterItem/components/CartIndicator";

const animationTime = 300; // ms

const AddToCartToaster: FC<{ cartModalOpen: boolean }> = ({
	cartModalOpen,
}) => {
	const appDispatch = useDispatchContext(AppDispatchContext);
	const [transformX, setTransformX] = useState<150 | 0>(150);

	const { toasterProduct, siteConfig, userCountry } = useAppState();

	const { t } = useTranslation();
	const { cartPath, checkoutPath } = useTranslatedPaths();
	const location = useLocation();
	const { itemsInCart } = useCartProducts();

	const removeToasterProduct = useCallback(() => {
		appDispatch({
			type: "setToasterProduct",
			toasterProduct: undefined,
		});
	}, [appDispatch]);

	const hideToaster = useCallback(() => {
		setTransformX(150);
		setTimeout(() => {
			removeToasterProduct();
		}, animationTime);
	}, [removeToasterProduct]);

	useEffect(() => {
		if (toasterProduct) {
			setTransformX(0);
			const timeout = setTimeout(hideToaster, 4000);
			return () => {
				removeToasterProduct();
				clearTimeout(timeout);
			};
		}
	}, [hideToaster, removeToasterProduct, toasterProduct]);

	// Pages ignored for the toaster
	// TODO: This should be changed to a more dynamic solution based on the page type

	const excludedPath =
		location.pathname.startsWith(cartPath) ||
		location.pathname.startsWith(checkoutPath) ||
		cartModalOpen;

	if (!toasterProduct || excludedPath) {
		if (toasterProduct) removeToasterProduct();
		return null;
	}

	const variantPrice = formatVariantPrice(
		toasterProduct.variant,
		siteConfig.currency,
		userCountry,
	);

	const variantColor = getVariantColorLabelFromFragment(
		toasterProduct.variant.attributes,
	);

	const variantFabric = getVariantFabricLabelFromFragment(
		toasterProduct.variant.attributes,
	);
	const variantImages = getVariantProductImages(toasterProduct.variant);
	const firstVariantImage = variantImages?.at(0);

	const variantImage = firstVariantImage
		? {
				src: firstVariantImage.imgSrc,
				alt: firstVariantImage.alt,
			}
		: undefined;

	const giftWrapMessage = siteConfig.product_page_gift_wrap_info;

	return (
		<>
			<ModalOverlay onClick={hideToaster} />
			<aside
				className={cn(s.addToCartToaster)}
				style={{
					transition: `all ${animationTime / 1000}s ease-in-out`,
					transform: `translateX(${transformX}%)`,
				}}
			>
				<div className={s.topRow}>
					<Button
						onClick={hideToaster}
						variant="container"
						iconLeft
						icon="close"
						buttonType="icon"
						label={t("Close")}
						className={s.close}
					/>
					<CartIndicator itemCount={itemsInCart} href={cartPath} />
				</div>
				<Divider variant="horizontal" />
				<ToasterItem
					name={toasterProduct.product.name || ""}
					price={variantPrice}
					color={variantColor}
					fabric={variantFabric}
					image={variantImage}
					giftWrapMessage={giftWrapMessage}
				/>
				<Divider variant="horizontal" />
				<Button
					variant="primary"
					icon="cart"
					alwaysSmall
					label={t(`Go to cart`)}
					href={cartPath}
					fullWidth
				/>
			</aside>
		</>
	);
};

export default AddToCartToaster;
